import { createFileRoute } from "@tanstack/react-router";

import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ContactForm } from "../components/home/ContactForm";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { Portfolio } from "../components/shared/Portfolio";
import { Reviews } from "../components/shared/Reviews";
import { Faq } from "../components/shared/Faq";
import type { FaqItem } from "../components/shared/Faq";
import { Showreel } from "../components/home/Showreel";
import { SitesHero } from "../components/sites/SitesHero";
import { SitesTypes } from "../components/sites/SitesTypes";
import { SitesScope } from "../components/sites/SitesScope";
import { SitesPricing } from "../components/sites/SitesPricing";
import { SitesProcess } from "../components/sites/SitesProcess";
import { SitesLinks } from "../components/sites/SitesLinks";
import {
  artlinemCase,
  archidicaCase,
  esgCase,
  systemLogisticCase,
  pokrovskieBaniCase,
  gbcvetCase,
} from "../content/casesAll";
import { SITE_URL, breadcrumbJsonLd, buildMeta, serviceJsonLd } from "../lib/site";
import { crumbTuples } from "../lib/breadcrumbs";

const title = "Разработка сайтов под ключ для бизнеса | HAY.agency";
const description =
  "Разрабатываем сайты под ключ: корпоративные сайты, лендинги, многостраничные сайты и каталоги. Аналитика, структура, дизайн, разработка, интеграции с CRM и SEO-основа.";

const faqItems: FaqItem[] = [
  {
    q: "Сколько стоит разработка сайта?",
    a: "Стоимость зависит от формата сайта и объёма работ. Лендинг или сайт на Tilda — от 250 000 ₽, корпоративный сайт — от 600 000 ₽. Точную оценку даём после обсуждения структуры, контента и интеграций.",
  },
  {
    q: "Сколько времени занимает разработка?",
    a: "Лендинг обычно занимает несколько недель, корпоративный сайт — дольше: срок зависит от количества разделов, готовности контента и сложности интеграций. Сроки фиксируем на этапе оценки.",
  },
  {
    q: "Чем лендинг отличается от корпоративного сайта?",
    a: "Лендинг решает одну сфокусированную задачу и рассчитан на рекламный трафик. Корпоративный сайт описывает компанию, услуги и экспертизу, развивается вместе с бизнесом и работает на органический трафик.",
  },
  {
    q: "На какой CMS вы делаете сайты?",
    a: "Подбираем технологию под задачу: Tilda для быстрых запусков, 1С-Битрикс для крупных корпоративных проектов, развитие существующей CMS или собственная разработка, когда логика выходит за рамки коробочного решения.",
  },
  {
    q: "Готовите ли вы тексты и контент?",
    a: "Да. Мы структурируем смыслы, готовим тексты страниц и офферы, помогаем с подбором и подготовкой визуальных материалов.",
  },
  {
    q: "Настраиваете ли вы интеграции с CRM?",
    a: "Да. Подключаем amoCRM, RetailCRM и Битрикс24, настраиваем передачу заявок, уведомления, аналитику и коллтрекинг.",
  },
  {
    q: "Что происходит после запуска сайта?",
    a: "Поддерживаем проект: дорабатываем разделы, следим за технической частью и развиваем сайт через SEO, контекстную рекламу и улучшение конверсионных сценариев.",
  },
];

export const Route = createFileRoute("/sites")({
  head: () => {
    const base = buildMeta({
      title,
      description,
      path: "/sites",
      image: `${SITE_URL}/og/services/sites.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Разработка сайтов под ключ — HAY.agency",
      locale: "ru_RU",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${SITE_URL}/sites#webpage`,
                url: `${SITE_URL}/sites`,
                name: title,
                description,
                inLanguage: "ru-RU",
                isPartOf: { "@id": `${SITE_URL}/#website` },
                breadcrumb: { "@id": `${SITE_URL}/sites#breadcrumbs` },
                mainEntity: { "@id": `${SITE_URL}/sites#service` },
              },
              serviceJsonLd({
                name: "Разработка сайтов под ключ",
                description,
                path: "/sites",
                id: `${SITE_URL}/sites#service`,
                serviceType: "Разработка сайтов",
                providerId: `${SITE_URL}/#organization`,
                inGraph: true,
              }),
              breadcrumbJsonLd(crumbTuples("/sites"), {
                id: `${SITE_URL}/sites#breadcrumbs`,
                inGraph: true,
              }),
              {
                "@type": "FAQPage",
                "@id": `${SITE_URL}/sites#faq`,
                mainEntity: faqItems.map((item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
              },
            ],
          }),
        },
      ],
    };
  },
  component: SitesPage,
});

function SitesPage() {
  return (
    <ServicePageLayout breadcrumb="Сайты" jsonLd={false}>
      <SitesHero />
      <div className="pb-10 lg:pb-[74px]">
        <ClientsMarquee bare />
      </div>
      <SitesTypes />
      <div className="pt-14 lg:pt-[110px]">
        <Showreel />
      </div>
      <SitesScope />
      <Portfolio
        title="Кейсы разработки сайтов"
        items={[
          artlinemCase,
          archidicaCase,
          esgCase,
          systemLogisticCase,
          pokrovskieBaniCase,
          gbcvetCase,
        ]}
        lead="Проекты, где мы отвечали за структуру, дизайн, разработку и развитие сайта после запуска."
      />
      <SitesPricing />
      <SitesProcess />
      <Faq
        compact
        title="Частые вопросы о разработке сайта"
        lead="Стоимость, сроки, форматы сайтов, CMS и интеграции — коротко."
        items={faqItems}
      />
      <SitesLinks />
      <Reviews />
      <ContactForm />
    </ServicePageLayout>
  );
}
